import { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import CrudModal from '../../components/admin/CrudModal';
import { adminDelete, adminGet, adminPost, adminPut } from '../../lib/adminApi';

const emptyTechnician = {
  name: '',
  specialty: '',
  phone: '',
  email: '',
  zone: '',
  status: 'Disponible',
  availability_note: '',
};

const AdminTechniciens = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [formState, setFormState] = useState(emptyTechnician);
  const [submitting, setSubmitting] = useState(false);

  const fetchTechnicians = async () => {
    try {
      setTechnicians(await adminGet('/admin/technicians'));
    } catch (error) {
      console.error('Error fetching technicians:', error);
      toast.error('Erreur lors du chargement des techniciens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const openCreateModal = () => {
    setEditingTechnician(null);
    setFormState(emptyTechnician);
    setModalOpen(true);
  };

  const openEditModal = (technician) => {
    setEditingTechnician(technician);
    setFormState({ ...emptyTechnician, ...technician });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (editingTechnician) {
        await adminPut(`/admin/technicians/${editingTechnician.id}`, formState);
        toast.success('Technicien mis à jour');
      } else {
        await adminPost('/admin/technicians', formState);
        toast.success('Technicien créé');
      }
      setModalOpen(false);
      fetchTechnicians();
    } catch (error) {
      console.error('Error saving technician:', error);
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (technicianId) => {
    if (!window.confirm('Supprimer ce technicien ?')) return;
    try {
      await adminDelete(`/admin/technicians/${technicianId}`);
      toast.success('Technicien supprimé');
      fetchTechnicians();
    } catch (error) {
      console.error('Error deleting technician:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleChange = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onAction={openCreateModal}>
      <Toaster position="top-right" richColors />

      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Techniciens</h2>
            <p className="crm-section-subtitle">
              Référentiel de l’équipe technique et de ses disponibilités.
            </p>
          </div>
        </div>

        <div className="crm-grid crm-grid--2">
          {technicians.length === 0 ? (
            <div className="crm-empty">Aucun technicien enregistré.</div>
          ) : (
            technicians.map((technician) => (
              <article key={technician.id} className="crm-mini-card">
                <div className="crm-mini-card__title">{technician.name}</div>
                <div className="crm-mini-card__meta">{technician.specialty}</div>
                <div className="crm-mini-card__row">
                  <span>{technician.zone || 'Zone non précisée'}</span>
                  <span className="crm-mini-badge">{technician.status}</span>
                </div>
                <div className="crm-mini-card__meta" style={{ marginTop: '10px' }}>
                  {technician.phone} {technician.email ? `· ${technician.email}` : ''}
                </div>
                {technician.availability_note ? (
                  <div className="crm-mini-card__meta" style={{ marginTop: '6px' }}>
                    {technician.availability_note}
                  </div>
                ) : null}
                <div className="crm-table__actions" style={{ marginTop: '12px' }}>
                  <button type="button" className="crm-action-link" onClick={() => openEditModal(technician)}>
                    Modifier
                  </button>
                  <button type="button" className="crm-action-danger" onClick={() => handleDelete(technician.id)}>
                    Supprimer
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      {modalOpen ? (
        <CrudModal
          title={editingTechnician ? 'Modifier un technicien' : 'Créer un technicien'}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={editingTechnician ? 'Mettre à jour' : 'Créer'}
        >
          <div className="crm-form__grid">
            <div className="crm-form__field">
              <label>Nom</label>
              <input value={formState.name} onChange={(e) => handleChange('name', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Spécialité</label>
              <input value={formState.specialty} onChange={(e) => handleChange('specialty', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Téléphone</label>
              <input value={formState.phone} onChange={(e) => handleChange('phone', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Email</label>
              <input type="email" value={formState.email} onChange={(e) => handleChange('email', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Zone</label>
              <input value={formState.zone} onChange={(e) => handleChange('zone', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Statut</label>
              <select value={formState.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="Disponible">Disponible</option>
                <option value="En mission">En mission</option>
                <option value="Atelier">Atelier</option>
                <option value="Absent">Absent</option>
              </select>
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Note de disponibilité</label>
              <textarea value={formState.availability_note} onChange={(e) => handleChange('availability_note', e.target.value)} />
            </div>
          </div>
        </CrudModal>
      ) : null}
    </AdminLayout>
  );
};

export default AdminTechniciens;
